import { ReactElement, useState } from 'react';
import { UIButton, UISpacer, UIText, UITextButton } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { Invite } from './invite';
import { NoInvites } from './no-invites';
import { useAcceptInviteAction } from '../actions';
import { useInvites } from '../providers';
import { useNavigate } from 'react-router';
import { useLogoutAction } from '@/me/actions';

type JoinOrCreateCompanyProps = {
    onCreateCompany?: () => void;
};

export function JoinOrCreateCompany(
    props: JoinOrCreateCompanyProps
): ReactElement {
    const { onCreateCompany } = props;

    const { t } = useCompaniesTranslation();

    const acceptInvite = useAcceptInviteAction();
    const { invites } = useInvites();
    const [loading, setLoading] = useState('');

    const navigate = useNavigate();
    const { logout, loading: loggingOut } = useLogoutAction();

    // No invites yet 😔
    // If your organisation already has a company set up, ask them to invite you. Your invite will show up here.
    return (
        <div>
            <UIText variant="h2" medium>
                {t('components.join_or_create_company.title')}
            </UIText>
            <UISpacer />
            <UIText>{t('components.join_or_create_company.text')}</UIText>

            <UISpacer multiplier={2} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {invites.map((invite) => (
                    <Invite
                        key={invite.id}
                        name={invite.companyName}
                        image={invite.companyImage}
                        acceptLoading={loading === invite.id}
                        disabled={loading !== ''}
                        allowDecline={false}
                        onAccept={() => {
                            setLoading(invite.id);
                            acceptInvite
                                .acceptInvite(invite.id)
                                .then((response) => {
                                    window.location.href = `/company/${response.data.company.id}`;
                                })
                                .catch(() => {}) // Error handled in the action
                                .finally(() => setLoading(''));
                        }}
                    />
                ))}
                {invites.length === 0 && <NoInvites />}
            </div>

            <UISpacer multiplier={2} />

            <UIText>
                {t('components.join_or_create_company.create_company_text')}
            </UIText>

            {invites.length > 0 && (
                <UITextButton
                    onClick={onCreateCompany}
                    disabled={loading !== ''}
                >
                    {t('components.join_or_create_company.create_company')}
                </UITextButton>
            )}
            {invites.length === 0 && (
                <>
                    <UISpacer />

                    <UIButton
                        variant={'primary'}
                        onClick={onCreateCompany}
                        disabled={loading !== ''}
                    >
                        {t('components.join_or_create_company.create_company')}
                    </UIButton>
                </>
            )}
            <UISpacer multiplier={2} />
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <UIText>
                    {t('components.join_or_create_company.switch_accounts')}
                    &nbsp;
                </UIText>
                <UITextButton
                    variant="primary"
                    loading={loggingOut}
                    disabled={loggingOut}
                    onClick={() => {
                        void logout().then(() => {
                            void navigate('/login', { replace: true });
                        });
                    }}
                >
                    {t('shared.labels.logout')}
                </UITextButton>
            </div>
        </div>
    );
}
